import { useEffect, useRef } from "react";
import { track } from "@/lib/analytics";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Centralized modal a11y: focus trap (Tab / Shift+Tab), Escape-to-close,
 * body scroll lock, focus restoration on unmount, and modal_open /
 * modal_close analytics. Designed to be dropped onto any custom dialog
 * that already renders role="dialog" + aria-modal="true".
 *
 * Returns a ref to attach to the dialog surface (the focus-trap root).
 */
export function useModalA11y(opts: {
  open: boolean;
  onClose: () => void;
  /** Stable identifier for analytics (e.g. "redemption", "legal_imprint"). */
  analyticsId: string;
  /** Element that should receive initial focus (typically the close button). */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
}) {
  const { open, onClose, analyticsId, initialFocusRef } = opts;
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    track("modal_open", { id: analyticsId });
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const getFocusable = (): HTMLElement[] => {
      const root = dialogRef.current;
      if (!root) return [];
      return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute("inert") && el.offsetParent !== null,
      );
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const focusable = getFocusable();
        if (focusable.length === 0) {
          e.preventDefault();
          dialogRef.current?.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement | null;
        const root = dialogRef.current;
        if (e.shiftKey && (active === first || !root?.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || !root?.contains(active))) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusRaf =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame(() => {
            const target = initialFocusRef?.current ?? dialogRef.current;
            target?.focus?.();
          })
        : (setTimeout(() => initialFocusRef?.current?.focus(), 0) as unknown as number);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(focusRaf);
      previouslyFocused?.focus?.();
      track("modal_close", { id: analyticsId });
    };
  }, [open, onClose, analyticsId, initialFocusRef]);

  return dialogRef;
}
